// Moved from `src/hooks/mutation/useMutateDeleteSessionsByIds.ts` (now a
// delegate) at ARCFE-994 — the deletion family's last residue and the
// program's most capture-coupled mutation. What moved is the SERVER half:
// the ordered delete, the version-dispatched optimistic list surgery and its
// rollback, the session-cache invalidations, and the domain-event emit. The
// OFFLINE half (six capture atoms, the Tauri bridge, tombstones) crosses as
// injected callables at their exact legacy positions — the cohort-C rule; no
// session → capture edge exists here, and every callable absent fails CLOSED
// to server-only behaviour. App reactions (toasts, analytics, integrations
// invalidations) cross as ports.
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { emitDomainEvent } from '@heidi/runtime/events';

import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
  GET_SESSION_BY_ID,
  GET_SESSIONS,
  sessionDeletedEvent,
  type QuerySessionResponse,
  type SessionsResponse,
} from '../../contracts';
import { deleteSessionByIds } from '../api/deletion';
import { sessionQueryVersionAtom } from '../atoms/sessionAtoms';
import {
  invalidateSessionsQuery,
  useCancelSessionsQuery,
} from '../query/sessionsListCache';
import { invalidateSessionById } from '../query/useQuerySessionById';

export const DELETE_SESSIONS_BY_IDS = 'DeleteSessionsByIds';

/**
 * The three id spaces one delete request can touch. Produced by the injected
 * resolver (the app composes offline queue + local-session state); the
 * default resolver treats every id as a server session — the fail-closed
 * shape for an app that wired no offline cluster.
 */
export type DeleteSessionsTargets = {
  serverSessionIds: string[];
  offlineLocalSessionIds: string[];
  cacheSessionIds: string[];
};

const unique = (values: string[]) => [...new Set(values)];

const defaultResolveTargets = (sessionIds: string[]): DeleteSessionsTargets => ({
  serverSessionIds: unique(sessionIds),
  offlineLocalSessionIds: [],
  cacheSessionIds: unique(sessionIds),
});

export type UseMutateDeleteSessionsByIdsProps = {
  /**
   * `'offlineFirst'` on desktop, `'online'` on web — the `isTauri()` read,
   * hoisted. Desktop deletes a local Tauri recording here, which must run
   * *because* we're offline; the default `'online'` pauses after `onMutate`
   * has already hidden the row, stranding the recording on disk. Web has no
   * local recordings, so it keeps the default and its deletes still queue
   * until reconnect. (With `retry: 0` — the mutation default —
   * `'offlineFirst'` and `'always'` behave identically; the former is the
   * narrower declaration.)
   */
  networkMode: 'offlineFirst' | 'online';
  /** Injected offline resolution — queue/local-session composition, app-side. */
  resolveTargets?: (
    sessionIds: string[],
    getCachedSession: (sessionId: string) => QuerySessionResponse | undefined
  ) => DeleteSessionsTargets;
  /** Injected capture command: delete local recordings (with its own
   * not-found swallow). Runs strictly AFTER the server delete. */
  deleteLocalRecordings?: (localSessionIds: string[]) => Promise<void>;
  /**
   * Should the server delete be QUEUED for later instead of sent now?
   *
   * INVOKED once per mutation, at the moment the request would have gone out,
   * rather than sampled when the mutation was created. So the answer is
   * whatever the app's most recently committed render produced, not a value
   * frozen earlier in the component's life.
   *
   * That is the whole of the guarantee, and it is narrower than "live". An app
   * predicate derived from render state cannot see a change that has not been
   * committed yet, so a connectivity drop landing in the gap between the last
   * commit and this call is still read as the previous state — the delete goes
   * to the backend and fails rather than queueing. Closing that gap would mean
   * reading the connectivity source at call time, which is a decision for the
   * app that owns the source, not something this port can promise.
   *
   * The app supplies the predicate; absent, nothing is ever deferred.
   */
  shouldDeferServerDelete?: () => boolean;
  /**
   * Injected durable queue: record that the backend is owed these deletes.
   *
   * REPLACES {@link deleteSessionByIds} on the deferred path and nothing else.
   * It MUST reject rather than silently succeed when the delete could not be
   * recorded — the row is already hidden optimistically, and the rejection is
   * the only thing that brings it back.
   */
  enqueueServerDeletes?: (serverSessionIds: string[]) => Promise<void>;
  /** Injected optimistic offline removal; returns an opaque rollback token. */
  applyOfflineOptimistic?: (targets: DeleteSessionsTargets) => unknown;
  /** Injected restore for the token above. */
  rollbackOffline?: (token: unknown) => void;
  /** App-reaction port, fired per deleted id where the legacy
   * generation-stop + side-by-side clear ran (beside the cache invalidate). */
  onSessionCleanup?: (sessionId: string) => void;
  /** App-reaction port: integrations invalidations + toast + analytics —
   * fired where that block ran, before the domain event. */
  onDeleted?: (input: { sessionIds: string[] }) => void;
  /** App-reaction port: the sanitised error log position. */
  onDeleteError?: (error: unknown) => void;
  /** App-reaction port: the error-toast position, after every rollback. */
  onDeleteFailed?: (input: { sessionIds: string[] }) => void;
};

export function useMutateDeleteSessionsByIds({
  networkMode,
  resolveTargets,
  deleteLocalRecordings,
  shouldDeferServerDelete,
  enqueueServerDeletes,
  applyOfflineOptimistic,
  rollbackOffline,
  onSessionCleanup,
  onDeleted,
  onDeleteError,
  onDeleteFailed,
}: UseMutateDeleteSessionsByIdsProps) {
  const queryClient = useQueryClient();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const { cancelSessionsQuery } = useCancelSessionsQuery();

  const getDeleteTargets = (sessionIds: string[]) => {
    const getCachedSession = (sessionId: string) =>
      queryClient.getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }]);
    return resolveTargets
      ? resolveTargets(sessionIds, getCachedSession)
      : defaultResolveTargets(sessionIds);
  };

  return useMutation({
    mutationKey: [DELETE_SESSIONS_BY_IDS],
    networkMode,
    mutationFn: async (input: { sessionIds: string[] }) => {
      const targets = getDeleteTargets(input.sessionIds);

      // Ordered, not concurrent: the local delete destroys the recording on
      // disk, which `onError` cannot undo — it only restores atoms and cache.
      // So nothing local is touched until the server copy is confirmed gone.
      // In a mixed batch a failing server delete throws first, skipping the
      // local-only rows too (unreachable today — synthetic rows can't be
      // bulk-selected).
      // Both ports, or neither: a defer predicate with nothing to defer TO
      // would drop the delete on the floor, so half a cluster falls back to
      // the request. Same fail-closed rule as the resolver above.
      const deferServerDelete =
        targets.serverSessionIds.length > 0 &&
        enqueueServerDeletes !== undefined &&
        shouldDeferServerDelete?.() === true;

      if (targets.serverSessionIds.length > 0) {
        if (deferServerDelete) {
          await enqueueServerDeletes(targets.serverSessionIds);
        } else {
          await deleteSessionByIds(targets.serverSessionIds);
        }
      }

      // A successful enqueue is NOT a completed deletion — the backend can
      // still refuse it, and `onError` restores a list row but cannot restore
      // a wiped file. So the audio is held until the server confirms; the
      // replay pass owns its removal, and the queued-delete guard stops it
      // being uploaded into a session that is being deleted meanwhile.
      //
      // A batch with no server ids never defers, so a purely local session
      // deleted offline still has its audio removed here, exactly as before.
      if (!deferServerDelete && targets.offlineLocalSessionIds.length > 0) {
        await deleteLocalRecordings?.(targets.offlineLocalSessionIds);
      }
    },
    onMutate: async ({ sessionIds }: { sessionIds: string[] }) => {
      await cancelSessionsQuery();
      const targets = getDeleteTargets(sessionIds);

      // The whole optimistic OFFLINE removal (atoms, tombstones, note-failed
      // clears across both id spaces) is the injected app half; the token it
      // returns is opaque here and only travels to `rollbackOffline`.
      const offlineRollback = applyOfflineOptimistic?.(targets);

      const updateInfiniteSessionsCache = (
        data: InfiniteData<SessionsResponse> | undefined
      ) => {
        if (!data) return data;
        if (data.pages.length === 0) return data;
        return {
          ...data,
          pages: data.pages.map((page) => {
            return {
              ...page,
              sessions: page.sessions.filter(
                (session) => !targets.cacheSessionIds.includes(session.session_id)
              ),
            };
          }),
        };
      };

      switch (sessionQueryVersion) {
        case 'v3': {
          // V3 has multiple cached queries with different filters, so snapshot
          // every match before the optimistic write to enable a full rollback.
          const previousQueries = queryClient.getQueriesData<
            InfiniteData<SessionsResponse>
          >({ queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION] });
          queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            { queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION] },
            updateInfiniteSessionsCache
          );
          return { previousQueries, offlineRollback };
        }
        case 'v2': {
          const previousQueries = queryClient.getQueriesData<
            InfiniteData<SessionsResponse>
          >({ queryKey: [GET_INFINITE_SESSIONS] });
          queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            { queryKey: [GET_INFINITE_SESSIONS] },
            updateInfiniteSessionsCache
          );
          return { previousQueries, offlineRollback };
        }
        default: {
          const previousQueries = queryClient.getQueriesData<SessionsResponse>({
            queryKey: [GET_SESSIONS],
          });
          queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldData) => {
            // Don't fabricate an entry when nothing is cached — otherwise the
            // snapshot (existing entries only) can't roll it back on failure.
            if (!oldData) return oldData;
            const filtered = oldData.sessions.filter(
              (session) => !targets.cacheSessionIds.includes(session.session_id)
            );
            return {
              ...oldData,
              count: filtered.length,
              has_next_page: oldData.has_next_page,
              sessions: filtered,
              total_count: filtered.length,
            };
          });
          return { previousQueries, offlineRollback };
        }
      }
    },
    onSuccess: async (_, input) => {
      const targets = getDeleteTargets(input.sessionIds);
      await invalidateSessionsQuery();

      // Invalidate individual session queries for each deleted session
      targets.cacheSessionIds.forEach((sessionId) => {
        onSessionCleanup?.(sessionId);
        invalidateSessionById(sessionId);
      });

      onDeleted?.(input);

      // Domain event (arch §5.2 Aperture 2): session's own mutation announces
      // the deletion; subscribers (tasks →) invalidate their own state.
      // Loss-tolerant notification — never a command. Announces the SERVER
      // identity of the deletion: the input ids plus any server-session
      // aliases resolved from the offline queue. Offline-LOCAL ids (disk-only
      // recordings) are deleted but not announced — they never had a server
      // identity for a subscriber to hold state against.
      emitDomainEvent(sessionDeletedEvent, { sessionIds: targets.cacheSessionIds });
    },
    onError: (error, input, context) => {
      onDeleteError?.(error);

      // Roll back the optimistic server-cache removal so failed deletes don't
      // leave the list diverged from server state.
      context?.previousQueries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      // Re-sync against the server to catch anything the snapshot missed
      // (e.g. the V4 list, or a concurrent change from another tab).
      void invalidateSessionsQuery();

      // Roll back the optimistic offline removal / tombstone so a failed delete
      // does not permanently hide a recording that still exists on disk/backend.
      if (context?.offlineRollback !== undefined) {
        rollbackOffline?.(context.offlineRollback);
      }

      onDeleteFailed?.(input);
    },
  });
}
